import React from 'react';
import { MotifTabNavigation, MotifTabControl } from '@ey-xd/motif-react';
import { Link } from 'react-router';
import { reduxForm } from 'redux-form';
import { withRouter } from '../../common/withRouter';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as authActions from '../../actions/authActions';
import * as clientActions from '../../actions/clientActions';
import * as analyticsEntitlementActions from '../../actions/analyticsEntitlementActions';
import PropTypes from 'prop-types';
import AnalyticsEntitlementTable from '../../components/utilities/AnalyticsEntitlementTable';

/**
 * Container component for managing utility-related views and functionality.
 *
 * Responsibilities:
 * - Handles tab navigation for utility features.
 * - Manages state for system administrator access, active tabs, and client/analytics data.
 * - Fetches and updates client and analytics entitlement data from Redux.
 * - Provides handlers for tab changes, grid updates, and save/cancel actions.
 * - Integrates with Motif tab components and Redux Form for state management.
 *
 * @component
 */
export class UtilitiesContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isSysAdmin: false,
      activeUtilityTab: 0,
      isSelectedClientChanged: false,
      entitlementData: [],
      gridCurrentData: [],
    };
    this.handleClick = this.handleClick.bind(this);
    this.handleUtilityTabClick = this.handleUtilityTabClick.bind(this);
    this.handleGridChanges = this.handleGridChanges.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSave = this.handleSave.bind(this);
  }

  /**
   * Invoked immediately after mounting occurs
   * @returns {undefined}
   */

  componentDidMount() {
    this.checkUserAuthorization();
    this.fetchAllClients();
    this.fetchAnalyticsEntitlementData();
  }

  checkUserAuthorization() {
    let isSysAdmin = false;
    if (Array.isArray(this.props.currentUserAuthorizations)) {
      isSysAdmin = authActions.isSystemAdministrator(
        this.props.currentUserAuthorizations
      );
    }
    this.setState({ isSysAdmin: isSysAdmin });
  }

  //Set utility tab index
  handleUtilityTabClick(index) {
    this.setState({ activeUtilityTab: index });
  }

  fetchAllClients() {
    // Return the promise so caller can await or .then()
    return this.props.clientActions.loadAllClients();
  }

  fetchAnalyticsEntitlementData() {
    return this.props.analyticsEntitlementActions
      .fetchAnalyticsEntitlement()
      .then((data) => {
        this.setState({ entitlementData: data });
      });
  }

  handleGridChanges(rowData) {
    const original = this.props.analyticsEntitlementData || [];

    const current = Array.isArray(rowData) ? rowData : [];

    // Build lookup for originals by analyticsReportName + clientID
    const origMap = new Map();
    original.forEach((o) => {
      const key = `${o.analyticsReportName}::${o.clientID}`;
      origMap.set(key, o);
    });

    // Find rows where isAccessGranted changed compared to original
    const selectedAnalyticsReports = current.filter((r) => {
      const key = `${r.analyticsReportName}::${r.clientID}`;
      const orig = origMap.get(key);
      if (!orig) return false;
      return (
        Boolean(orig.isAccessGranted) !== Boolean(r.isAccessGranted) ||
        Boolean(orig.isAssignedToAllClients) !==
          Boolean(r.isAssignedToAllClients)
      );
    });

    this.setState({
      isSelectedClientChanged: selectedAnalyticsReports.length > 0,
      gridCurrentData: current,
    });
  }

  handleCancel() {
    this.setState(
      {
        isSelectedClientChanged: false,
      },
      () => {
        this.fetchAnalyticsEntitlementData();
      }
    );
  }

  handleSave() {
    // get only changed rows for isAccessGranted
    const rowsToSaveForAccessGranted = this.state.gridCurrentData.filter(
      (r) => {
        const orig = this.props.analyticsEntitlementData.find(
          (o) =>
            o.analyticsReportName === r.analyticsReportName &&
            o.clientID === r.clientID
        );
        if (!orig) return false;
        return Boolean(orig.isAccessGranted) !== Boolean(r.isAccessGranted);
      }
    );

    // get only changed rows for isAssignedToAllClients
    const rowsToSaveForAssignedAllClients = this.state.gridCurrentData.filter(
      (r) => {
        const orig = this.props.analyticsEntitlementData.find(
          (o) =>
            o.analyticsReportName === r.analyticsReportName &&
            o.clientID === r.clientID
        );
        if (!orig) return false;
        return (
          Boolean(orig.isAssignedToAllClients) !==
          Boolean(r.isAssignedToAllClients)
        );
      }
    );

    // Group the assigned-to-all-client changes by AnalyticsReportCatalogID
    const groupedAssignedByCatalog = Object.values(
      rowsToSaveForAssignedAllClients.reduce((acc, row) => {
        // normalize possible property name variations
        const catalogId =
          row.analyticsReportCatalogID ??
          row.AnalyticsReportCatalogID ??
          row.analyticsReportCatalogId ??
          'unknown';
        const key = String(catalogId);

        if (!acc[key]) {
          acc[key] = {
            analyticsReportCatalogID: catalogId,
            isAssignedToAllClients: Boolean(row.isAssignedToAllClients),
          };
        }

        return acc;
      }, {})
    );

    const request = {
      AnalyticsEntitlementDetail: rowsToSaveForAccessGranted,
      AnalyticsEntitlementReportCatalogDetail: groupedAssignedByCatalog,
    };

    this.props.analyticsEntitlementActions
      .manageAnalyticsEntitlement(request)
      .then(() => {
        this.fetchAnalyticsEntitlementData();
        this.setState({ isSelectedClientChanged: false });
      })
      .catch((error) => {
        console.log(
          'Error occurred while processing the creation or update of Analytics Entitlement.',
          error
        );
      });
  }

  render() {
    // Non sys admins are unauthorized to access system admin page
    if (!this.state.isSysAdmin) {
      this.props.router.navigate(`/unauthorized`);
      return <span />;
    }

    let isAnalyticsFeatureEnabled =
      this.props.featureManagementData &&
      this.props.featureManagementData['isAnalyticsFeatureEnabled'];

    let entitlementData = this.state.entitlementData || [];
    return (
      <React.Fragment>
        <h2 data-testid="pghdrSystemAdministrator">System Administrators</h2>
        <MotifTabNavigation variant="default">
          <Link to={`/adminauth`} tabIndex="-1">
            <MotifTabControl
              data-testid="tbManageSystemAdministrator"
              role="tab"
              onClick={() => this.handleClick(1)}
            >
              Manage System Administrators
            </MotifTabControl>
          </Link>
          <Link to={`/adminauth/dataDeletionDiagnostic`}>
            <MotifTabControl
              role="tab"
              data-testid="tbDataDeletionDiagnostic"
              onClick={() => this.handleClick(2)}
            >
              Data Deletion Diagnostic
            </MotifTabControl>
          </Link>
          <Link to={`/adminauth/externalUserDataVisibility`} tabIndex="-1">
            <MotifTabControl
              role="tab"
              data-testid="tbExternalUserDataVisibilityManager"
              onClick={() => this.handleClick(3)}
            >
              External User Data Visibility Manager
            </MotifTabControl>
          </Link>
          {isAnalyticsFeatureEnabled ? (
            <Link to={`/adminauth/utilities`} tabIndex="-1">
              <MotifTabControl
                className="motif-active"
                role="tab"
                data-testid="tbUtilities"
                onClick={() => this.handleClick(4)}
              >
                Utilities
              </MotifTabControl>
            </Link>
          ) : (
            <></>
          )}
        </MotifTabNavigation>

        {/* Vertical Tab Navigation for Utilities */}
        <div className="row mt-3">
          <div className="field col-md-2 pt-4 pr-0">
            <MotifTabNavigation
              defaultActiveKey={0}
              variant="default"
              orientation="vertical"
              lineSide="right"
            >
              <MotifTabControl
                className="d-flex align-items-center pt-2 pb-2 tabNavigation-hover"
                style={{ background: '#FFF', marginBottom: '1px' }}
                role="tab"
                data-testid="tbAnalyticsEntitlement"
                onClick={() => this.handleUtilityTabClick(0)}
              >
                <span
                  className="verticalTabNavigation"
                  data-testid="lblAnalyticsEntitlementHelpGuide"
                >
                  Analytics Entitlement
                </span>
              </MotifTabControl>

              {/* Future tabs can be added here */}
            </MotifTabNavigation>
          </div>

          <div className="field col-md-10 standardPadding">
            {this.state.activeUtilityTab === 0 && (
              <div>
                <AnalyticsEntitlementTable
                  analyticsEntitlementData={entitlementData}
                  handleGridChanges={this.handleGridChanges}
                  clientCount={this.props.clientData?.length || 0}
                  isSelectedClientChanged={this.state.isSelectedClientChanged}
                  handleCancel={this.handleCancel}
                  handleSave={this.handleSave}
                />
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

UtilitiesContainer.propTypes = {
  clientId: PropTypes.number,
  currentUserAuthorizations: PropTypes.array,
  authActions: PropTypes.object,
  clientActions: PropTypes.object,
  router: PropTypes.object,
  analyticsEntitlementActions: PropTypes.object,
  clientData: PropTypes.array,
  analyticsEntitlementData: PropTypes.array,
  featureManagementData: PropTypes.object,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state) {
  return {
    currentUserAuthorizations: state.currentUserAuthorizations,
    clientData: state.clients,
    analyticsEntitlementData: state.analyticsEntitlementData,
    featureManagementData: state.featureManagementData,
  };
}
/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    authActions: bindActionCreators(authActions, dispatch),
    clientActions: bindActionCreators(clientActions, dispatch),
    analyticsEntitlementActions: bindActionCreators(
      analyticsEntitlementActions,
      dispatch
    ),
  };
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(
    reduxForm({ form: 'UtilitiesContainer', enableReinitialize: true })(
      UtilitiesContainer
    )
  )
);
